import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Droplets, 
  Brain, 
  Waves, 
  Zap, 
  Check, 
  Leaf, 
  ArrowRight,
  ChevronDown,
  Share2,
  Copy,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product Images
const PRODUCT_FRONT = "https://customer-assets.emergentagent.com/job_83eadd35-d704-496d-9c40-8c6db371c3f3/artifacts/at56tfcm_Amazon%20Shot%20White%20Background.png";
const PRODUCT_BACK = "https://customer-assets.emergentagent.com/job_83eadd35-d704-496d-9c40-8c6db371c3f3/artifacts/f25fhxo3_Amazon%20shot%20white%20backgorund%20back.png";
const LIFESTYLE_IMAGE = "https://customer-assets.emergentagent.com/job_naturt-coming-soon/artifacts/29oo62zv_generated-image.png";

// Animation variants
const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Referral Component
const ReferralSection = () => {
  const [copied, setCopied] = useState(false);
  const referralLink = typeof window !== 'undefined' ? window.location.href + "?ref=friend" : "";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Link copied! Share with friends for priority access.");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Couldn't copy link. Please try again.");
    }
  };

  const shareOnTwitter = () => {
    const text = "I just signed up for early access to NaturT Flow — calm focus hydration without caffeine. Join me!";
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 p-6 bg-gradient-to-br from-[#FFDAB9]/30 to-[#E6F8F7]/50 rounded-2xl border border-[#00B8A9]/10"
      data-testid="referral-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-[#FF7F50]" />
        <span className="font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Get Priority Access
        </span>
      </div>
      <p className="text-sm text-[#4B5563] mb-4">
        Share with friends and move up the waitlist. More referrals = earlier access.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={copyToClipboard}
          data-testid="copy-referral-btn"
          className="flex-1 h-11 bg-white text-[#0A0A0A] rounded-full px-5 font-medium border border-[#0A0A0A]/10 hover:border-[#00B8A9] transition-all inline-flex items-center justify-center gap-2 text-sm"
        >
          {copied ? <Check className="w-4 h-4 text-[#00B8A9]" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied!" : "Copy Link"}
        </button>
        <button
          onClick={shareOnTwitter}
          data-testid="share-twitter-btn"
          className="flex-1 h-11 bg-[#0A0A0A] text-white rounded-full px-5 font-medium hover:bg-[#1a1a1a] transition-all inline-flex items-center justify-center gap-2 text-sm"
        >
          <Share2 className="w-4 h-4" />
          Share on X
        </button>
      </div>
    </motion.div>
  );
};

// Email Form Component
const KLAVIYO_LIST_ID = "WSDhEY";
const KLAVIYO_COMPANY_ID = "XJvirS";

const EmailForm = ({ variant = "hero", showReferral = true }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const subscribeToKlaviyo = async (emailAddress) => {
    try {
      await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2023-02-22'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'NaturT Flow Website',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: emailAddress
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: KLAVIYO_LIST_ID
                }
              }
            }
          }
        })
      });
    } catch (error) {
      console.error('Klaviyo subscription error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      // Subscribe to Klaviyo
      await subscribeToKlaviyo(email);
      
      // Also save to MongoDB backend
      const response = await axios.post(`${API}/subscribe`, { email });
      if (response.data.success) {
        setSubmitted(true);
        setEmail("");
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-md">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 bg-[#E6F8F7] rounded-full px-6 py-4"
          data-testid={`${variant}-success-message`}
        >
          <div className="w-8 h-8 rounded-full bg-[#00B8A9] flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
          <span className="text-[#0A0A0A] font-medium">You're on the list!</span>
        </motion.div>
        {showReferral && <ReferralSection />}
      </div>
    );
  }

  const microcopy = variant === "hero" 
    ? "Limited first batch — be first to try NaturT Flow"
    : "First batch will be limited";

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          data-testid={`${variant}-email-input`}
          className="h-14 rounded-full bg-white border border-[#0A0A0A]/10 px-6 focus:ring-2 focus:ring-[#00B8A9]/30 focus:border-[#00B8A9] outline-none w-full shadow-sm text-[#0A0A0A] placeholder:text-[#9CA3AF]"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`${variant}-submit-btn`}
          className="btn-premium h-14 bg-[#00B8A9] text-white rounded-full px-8 font-medium hover:bg-[#009A8D] transition-all shadow-[0_8px_24px_rgba(0,184,169,0.3)] hover:shadow-[0_12px_32px_rgba(0,184,169,0.4)] hover:-translate-y-1 inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Join Early Access
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
      <p className="text-xs text-[#9CA3AF] mt-3 text-center sm:text-left">
        {microcopy}
      </p>
    </form>
  );
};

// Header Component
const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = () => {
    document.getElementById("hero-email")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-white/80 backdrop-blur-lg border-b border-[#0A0A0A]/5 shadow-sm" 
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="logo">
          <span className="text-xl md:text-2xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            NaturT <span className="text-[#00B8A9]">Flow</span>
          </span>
        </div>
        <button 
          onClick={scrollToSection}
          data-testid="header-cta-btn"
          className="hidden sm:flex h-10 bg-[#00B8A9] text-white rounded-full px-6 font-medium hover:bg-[#009A8D] transition-all items-center justify-center text-sm"
        >
          Join Early Access
        </button>
      </div>
    </header>
  );
};

// Hero Section - UPDATED
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-12 overflow-hidden" data-testid="hero-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFDAB9]/20 via-white to-[#E6F8F7]/40" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#00B8A9]/5 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left content */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left"
          >
            <motion.div variants={fadeUpVariant} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6F8F7] text-[#00B8A9] text-sm font-medium">
                <Leaf className="w-4 h-4" />
                Coming Soon
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeUpVariant}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#0A0A0A] tracking-tight leading-tight mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Not a boost. A baseline.
            </motion.h1>
            
            <motion.p 
              variants={fadeUpVariant}
              className="text-lg md:text-xl text-[#00B8A9] font-medium max-w-xl mx-auto lg:mx-0 mb-3"
            >
              Clean focus, calm energy, and real hydration — all in one daily drink.
            </motion.p>
            
            <motion.p 
              variants={fadeUpVariant}
              className="text-base text-[#4B5563] max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Feel sharper. Think clearer. Stay in control.
            </motion.p>
            
            <motion.div variants={fadeUpVariant} id="hero-email" className="flex justify-center lg:justify-start">
              <EmailForm variant="hero" />
            </motion.div>
          </motion.div>
          
          {/* Right - Product Image */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex justify-center"
          >
            <div className="relative">
              {/* Glow effect behind product */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#00B8A9]/20 to-[#FFDAB9]/30 blur-3xl rounded-full scale-75 animate-pulse-soft" />
              
              <img 
                src={PRODUCT_FRONT}
                alt="NaturT Flow Product"
                className="relative z-10 w-full max-w-md lg:max-w-lg animate-float object-contain mix-blend-multiply"
                data-testid="hero-product-image"
              />
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
        >
          <span className="text-xs text-[#9CA3AF]">Scroll to explore</span>
          <ChevronDown className="w-5 h-5 text-[#00B8A9] animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Trust Strip - UPDATED
const trustStripItems = [
  "No Sugar",
  "No Caffeine", 
  "100% Natural Ingredients",
  "No Artificial Sweeteners",
  "Designed for Daily Performance"
];

const TrustStrip = () => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="py-8 md:py-12 bg-white border-y border-[#0A0A0A]/5"
      data-testid="trust-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
          {trustStripItems.map((item, index) => (
            <div 
              key={item}
              className="flex items-center gap-2"
              data-testid={`trust-strip-item-${index}`}
            >
              <div className="w-5 h-5 rounded-full bg-[#00B8A9] flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm md:text-base text-[#4B5563] font-medium whitespace-nowrap">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Benefits Section - ANIMATED EMOJI STYLE
const BEACH_LIFESTYLE = "https://customer-assets.emergentagent.com/job_naturt-coming-soon/artifacts/63v1amjq_Refreshing%20mango-peach%20hydration%20moment.png";

const benefits = [
  {
    emoji: "💧",
    bgColor: "bg-[#E0F4F7]",
    title: "Hydration",
    description: "Electrolytes that actually hydrate your body."
  },
  {
    emoji: "🧠",
    bgColor: "bg-[#FCE8E8]",
    title: "Focus",
    description: "Stay sharp and mentally clear."
  },
  {
    emoji: "😌",
    bgColor: "bg-[#E8F5E0]",
    title: "Calm Mind",
    description: "Smooth, sustained clarity — no jitters."
  },
  {
    emoji: "⚡",
    bgColor: "bg-[#F5F0E0]",
    title: "Zero Crash",
    description: "No caffeine. No sugar. No spikes."
  }
];

const BenefitsSection = () => {
  return (
    <section className="py-28 md:py-36 bg-[#FDF8F3]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Benefits cards */}
          <div>
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariant}
              className="mb-12"
            >
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B8A9] mb-4 block">
                Why NaturT Flow
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Everything you need. Nothing you don't.
              </h2>
            </motion.div>
            
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-4"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  variants={fadeUpVariant}
                  className="bg-white rounded-2xl p-6 shadow-[0_2px_16px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] group"
                  data-testid={`benefit-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${benefit.bgColor} flex items-center justify-center flex-shrink-0 group-hover:animate-bounce-soft`}>
                      <span className="text-2xl">{benefit.emoji}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {benefit.title}
                      </h3>
                      <p className="text-[#6B7280] text-sm leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
          
          {/* Right side - Beach lifestyle image */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)]">
              <img 
                src={BEACH_LIFESTYLE}
                alt="Woman enjoying NaturT Flow at the beach"
                className="w-full h-auto max-h-[650px] object-contain"
                data-testid="beach-lifestyle-image"
              />
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#00B8A9]/10 rounded-full blur-2xl" />
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FFDAB9]/40 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Product Experience Section - UPDATED
const ProductExperienceSection = () => {
  return (
    <section className="py-28 md:py-36 relative overflow-hidden" data-testid="experience-section">
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#E6F8F7]/30 to-[#FFDAB9]/20" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image side */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-3xl overflow-hidden glow-teal">
              <img 
                src={LIFESTYLE_IMAGE}
                alt="NaturT Flow drink with mango peach"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="lifestyle-image"
              />
              {/* Brand statement overlay */}
              <div className="absolute bottom-3 left-6 right-6 bg-white/90 backdrop-blur-xl rounded-2xl p-5 border border-white/50">
                <p className="text-[#0A0A0A] font-medium text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your daily ritual for calm focus.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Content side */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="order-1 lg:order-2"
          >
            <motion.span 
              variants={fadeUpVariant}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B8A9] mb-4 block"
            >
              The Experience
            </motion.span>
            <motion.h2 
              variants={fadeUpVariant}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Feel sharper. Think clearer. Stay calm.
            </motion.h2>
            <motion.p 
              variants={fadeUpVariant}
              className="text-lg text-[#4B5563] mb-8 leading-relaxed"
            >
              This isn't just hydration. It's how you stay sharp through your day — from work to gym to everything in between.
            </motion.p>
            <motion.div variants={fadeUpVariant} className="flex flex-wrap gap-3">
              {["Mango Peach Flavour", "Caffeine-Free", "KSM-66 Ashwagandha"].map((tag) => (
                <span 
                  key={tag}
                  className="px-4 py-2 rounded-full bg-[#E6F8F7] text-[#00B8A9] text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// NEW: What's Inside / Formula Section
const formulaIngredients = [
  { name: "L-Tyrosine", dose: "750mg", benefit: "Focus and drive" },
  { name: "L-Theanine", dose: "200mg", benefit: "Calm, controlled clarity" },
  { name: "KSM-66 Ashwagandha", dose: "300mg", benefit: "Stress balance and mental control" },
  { name: "Electrolyte Complex", dose: "1000mg", benefit: "Real hydration support" },
  { name: "Coconut Water Powder", dose: "1000mg", benefit: "Natural electrolytes" },
  { name: "Magnesium Bisglycinate", dose: "150mg", benefit: "Muscle function" },
  { name: "Vitamin C", dose: "200mg", benefit: "Immune support" },
  { name: "B12 (Methylcobalamin)", dose: "10μg", benefit: "Energy metabolism" }
];

const FormulaSection = () => {
  return (
    <section className="py-28 md:py-36 bg-white" data-testid="formula-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariant}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B8A9] mb-4 block">
            The Formula
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Clinically dosed. Nothing hidden.
          </h2>
          <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
            Every ingredient in NaturT Flow is listed with its exact dose. No proprietary blends. No underdosed fillers.
          </p>
        </motion.div>
        
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto"
        >
          {formulaIngredients.map((ingredient, index) => (
            <motion.div
              key={ingredient.name}
              variants={fadeUpVariant}
              className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-xl border border-[#0A0A0A]/5"
              data-testid={`formula-ingredient-${index}`}
            >
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#00B8A9] flex-shrink-0" />
                <div>
                  <span className="font-medium text-[#0A0A0A]">{ingredient.name}</span>
                  <span className="text-[#9CA3AF] text-sm ml-2">— {ingredient.benefit}</span>
                </div>
              </div>
              <span className="text-sm font-semibold text-[#00B8A9] ml-4 whitespace-nowrap">{ingredient.dose}</span>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[#4B5563] mt-10 text-base"
        >
          Built with ingredients that work with your body — not against it.
        </motion.p>
      </div>
    </section>
  );
};

// NEW: Evidence Section
const evidenceCards = [
  {
    title: "KSM-66 Ashwagandha",
    description: "Studied across 21 published clinical trials for stress adaptation, cortisol reduction, and cognitive performance."
  },
  {
    title: "L-Theanine 200mg",
    description: "The dose used in peer-reviewed research on sustained attention and calm alertness — without drowsiness."
  },
  {
    title: "L-Tyrosine 750mg",
    description: "Used in military and academic research studying cognitive function under stress and fatigue."
  }
];

const EvidenceSection = () => {
  return (
    <section className="py-28 md:py-36 bg-[#F9FAFB]" data-testid="evidence-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariant}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B8A9] mb-4 block">
            The Formula Speaks for Itself
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Built on evidence, not hype.
          </h2>
        </motion.div>
        
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {evidenceCards.map((card, index) => (
            <motion.div
              key={card.title}
              variants={fadeUpVariant}
              className="bg-white rounded-3xl p-8 border border-[#0A0A0A]/5 shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
              data-testid={`evidence-card-${index}`}
            >
              {/* Molecule/Leaf icon instead of stars */}
              <div className="w-10 h-10 rounded-xl bg-[#E6F8F7] flex items-center justify-center mb-6">
                <Leaf className="w-5 h-5 text-[#00B8A9]" strokeWidth={1.5} />
              </div>
              
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {card.title}
              </h3>
              
              <p className="text-[#4B5563] leading-relaxed">
                {card.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[#4B5563] mt-12 text-base"
        >
          Be one of the first to try it. Early access subscribers will receive a sample request form after sign-up.
        </motion.p>
      </div>
    </section>
  );
};

// Final CTA Section - UPDATED
const FinalCTASection = () => {
  return (
    <section className="py-28 md:py-36 relative overflow-hidden" data-testid="final-cta-section">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFDAB9]/40 via-[#FF7F50]/20 to-[#00B8A9]/20" />
      <div className="absolute top-0 left-0 w-full h-full opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00B8A9]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FFDAB9]/40 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUpVariant} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur text-[#FF7F50] text-sm font-semibold">
              <Zap className="w-4 h-4" />
              Launching Soon · Limited First Batch Only
            </span>
          </motion.div>
          
          <motion.h2 
            variants={fadeUpVariant}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] tracking-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Don't miss the drop.
          </motion.h2>
          
          <motion.p 
            variants={fadeUpVariant}
            className="text-lg md:text-xl text-[#4B5563] max-w-2xl mx-auto mb-10"
          >
            Join the waitlist and be first in line when we launch.
          </motion.p>
          
          <motion.div variants={fadeUpVariant} className="flex justify-center">
            <EmailForm variant="cta" showReferral={false} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer - UPDATED
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-[#0A0A0A]/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              NaturT <span className="text-[#00B8A9]">Flow</span>
            </span>
          </div>
          
          <p className="text-[#9CA3AF] text-sm text-center">
            © 2026 NaturT Labs. All rights reserved.
          </p>
          
          <div className="flex items-center gap-6 text-sm text-[#4B5563]">
            <a href="https://www.naturtiabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#00B8A9] transition-colors">
              naturtiabs.com
            </a>
          </div>
        </div>
        
        {/* Regulatory disclaimer */}
        <p className="text-xs text-[#9CA3AF] text-center max-w-4xl mx-auto leading-relaxed">
          These statements have not been evaluated by the MHRA. NaturT Flow is a food supplement and is not intended to diagnose, treat, cure, or prevent any disease. Not a substitute for a varied and balanced diet.
        </p>
      </div>
    </footer>
  );
};

// Mobile Sticky CTA
const MobileStickyCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToEmail = () => {
    document.getElementById("hero-email")?.scrollIntoView({ behavior: "smooth" });
  };

  if (!visible) return null;

  return (
    <motion.div 
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-lg border-t border-[#0A0A0A]/5 z-50 md:hidden"
      data-testid="mobile-sticky-cta"
    >
      <button
        onClick={scrollToEmail}
        className="w-full h-12 bg-[#00B8A9] text-white rounded-full font-medium hover:bg-[#009A8D] transition-all shadow-[0_8px_24px_rgba(0,184,169,0.3)] flex items-center justify-center gap-2"
        data-testid="mobile-sticky-cta-btn"
      >
        Join Early Access
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// Main Landing Page - UPDATED STRUCTURE
const LandingPage = () => {

useEffect(() => {
  const script1 = document.createElement("script");
  script1.async = true;
  script1.src = "https://www.googletagmanager.com/gtag/js?id=G-N81LBK0K4W";
  document.head.appendChild(script1);

  const script2 = document.createElement("script");
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-N81LBK0K4W');
  `;
  document.head.appendChild(script2);
}, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      <HeroSection />
      <TrustStrip />
      <BenefitsSection />
      <ProductExperienceSection />
      <FormulaSection />
      <EvidenceSection />
      <FinalCTASection />
      <Footer />
      <MobileStickyCTA />
    </div>
  );
};

export default LandingPage;
